// One meeting session on screen. The Daily call (useDailyCall), chat, invite / waiting room and drawing board live
// HERE, and this component stays mounted for the whole session; only the frame around it changes (the floating
// window, the corner tile, its own pop-out window, the guest page), so moving or resizing never drops the call.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { WifiOff } from 'lucide-react';
import { useDailyCall } from './useDailyCall';
import { useMeetingChat } from './useMeetingChat';
import { useMeetingShare } from './useMeetingShare';
import { useHostHandoff } from './useHostHandoff';
import HostDialogs from './HostDialogs';
import { usePopout } from './usePopout';
import { useFloatingWindow } from './useFloatingWindow';
import { MediaEl, aTrack, handOrder, sTrack } from './media';
import CallStage from './CallStage';
import Toolbar from './Toolbar';
import TitleBar from './TitleBar';
import SidePanel from './SidePanel';
import CallLobby from './CallLobby';
import { CallWindow, FullFrame, GuestFrame, MiniFrame, PoppedPill } from './frames';

const VIEW_KEY = 'strata.meeting.view';
const pill = 'rounded-full px-4 py-1.5 text-sm font-semibold text-white';

function Dropped({ dc }) {
  if (dc.phase === 'ended') {
    return (
      <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-[#0D1B2A]/95 p-4 text-center text-[#E8EEF4]" data-testid="mm-ended">
        <p className="text-sm font-semibold">This meeting has ended.</p>
        <button type="button" onClick={dc.leave} className={`${pill} bg-[#4575A8] hover:bg-[#3A6491]`} data-testid="mm-ended-close">Close</button>
      </div>
    );
  }
  if (dc.phase !== 'left') return null;
  return (
    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-[#0D1B2A]/90 p-4 text-center text-[#E8EEF4]" data-testid="mm-dropped">
      <WifiOff className="h-6 w-6 text-amber-300" />
      <p className="text-sm font-semibold">You got disconnected from the meeting.</p>
      {dc.error && <p className="max-w-sm text-xs text-[#8AA3BC]" data-testid="mm-room-error">{dc.error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={dc.join} className={`${pill} bg-[#4575A8] hover:bg-[#3A6491]`} data-testid="mm-rejoin">Rejoin</button>
        <button type="button" onClick={dc.leave} className={`${pill} bg-white/15 hover:bg-white/25`} data-testid="mm-dropped-leave">Leave</button>
      </div>
    </div>
  );
}

// Someone is in the waiting room and the Participants panel is closed: a Zoom-like nudge on the stage.
function WaitingNudge({ share, open }) {
  const r = share?.canAdmit && !open ? share.requests[0] : null;
  if (!r) return null;
  const more = share.requests.length - 1;
  return (
    <div className="absolute right-3 top-3 z-20 w-72 rounded-xl border border-white/10 bg-[#0D1B2A] p-3 text-sm text-white shadow-2xl" data-testid="mm-waiting-nudge">
      <p><span className="font-semibold">{r.name}</span>{more > 0 ? ` and ${more} more are` : ' is'} waiting to join.</p>
      <div className="mt-2 flex gap-2">
        <button type="button" className="rounded-md bg-[#4575A8] px-3 py-1 text-xs font-semibold hover:bg-[#3A6491]" disabled={share.deciding === r.request_id}
          onClick={() => share.decide(r.request_id, 'admit')} data-testid="mm-waiting-admit">Admit</button>
        <button type="button" className="rounded-md bg-white/10 px-3 py-1 text-xs font-semibold hover:bg-white/20" onClick={share.openPanel}
          data-testid="mm-waiting-view">See waiting room</button>
      </div>
    </div>
  );
}

// Remote audio stays in this tab's document whatever frame the video is in, through the chosen speaker.
function RemoteAudio({ dc }) {
  return Object.values(dc.participants).filter((p) => p && !p.local)
    .map((p) => <MediaEl key={`a-${p.session_id}`} kind="audio" track={aTrack(p)} sinkId={dc.devices.speakerId} />);
}

// Hosts hear about raised hands (a toast in the app tab).
function useHandToasts(dc, hands, canLower) {
  const prev = useRef(new Set());
  useEffect(() => {
    const cur = new Set(hands.keys());
    if (canLower) {
      cur.forEach((id) => {
        if (!prev.current.has(id) && id !== dc.local?.session_id) toast(`${dc.participants[id]?.user_name || 'Someone'} raised a hand`);
      });
    }
    prev.current = cur;
  }, [hands, dc.local, dc.participants, canLower]);
}

export default function CallSession({ session, mode, title, host, onExpand, onMinimize, onLeave }) {
  const dc = useDailyCall({ roomUrl: session.room_url, token: session.token, onLeave, hostId: session.host_uid });
  const handoff = useHostHandoff({ dc, sessionId: session.session_id, enabled: !!host });
  const [ask, setAsk] = useState(null);
  const canLower = !!dc.local?.owner || handoff.isHost;
  const pop = usePopout(title);
  // The host leaving with other team members here picks a successor first (from the full window); else steps down.
  const hostLeave = (prep) => {
    if (handoff.isHost && handoff.members.length) { prep?.(); setAsk('leave'); } else handoff.leaveAsHost();
  };
  const powers = host ? {
    canAdmit: !!host.canManage || handoff.isHost,
    canEndAll: handoff.isHost,
    askLeave: () => hostLeave(),
    askEnd: () => setAsk('end'),
  } : null;
  const [panel, setPanel] = useState(null);
  // Unread counts whenever the chat isn't actually on screen (closed, or the call is in the corner tile).
  const chat = useMeetingChat(dc, panel === 'chat' && (!!pop.root || mode !== 'mini'));
  const share = useMeetingShare(host && session.session_id ? session.session_id : null, !!powers?.canAdmit);
  const win = useFloatingWindow();
  const [view, setViewState] = useState(() => { try { return localStorage.getItem(VIEW_KEY) || 'speaker'; } catch { return 'speaker'; } });
  const [annotate, setAnnotate] = useState(false);
  const boardRef = useRef(null);
  const canvasRef = useRef(null);
  const hands = useMemo(() => handOrder(dc.participants), [dc.participants]);
  const sharing = useMemo(() => Object.values(dc.participants).some((p) => sTrack(p)), [dc.participants]);
  const people = Object.keys(dc.participants).length;
  useHandToasts(dc, hands, canLower);

  useEffect(() => { if (!sharing) { boardRef.current = null; setAnnotate(false); } }, [sharing]);

  const setView = (v) => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ } };
  const clear = () => { canvasRef.current?.clearAll(); dc.send({ mm: 'clear' }); };
  const shareCtl = host && session.session_id ? { ...share, openPanel: () => setPanel('participants') } : null;

  const inner = (where) => {
    const width = where === 'popout' ? pop.width : mode === 'standalone' ? window.innerWidth : win.rect.w;
    const narrow = width < 760;
    return (
      <>
        <TitleBar title={title} since={dc.joinedAt} people={people} view={view} setView={setView} narrow={narrow}
          onMinimize={where === 'app' && mode === 'window' ? onMinimize : null}
          pop={where === 'app' ? { supported: pop.supported, open: () => { pop.open(); } } : null}
          dragProps={where === 'app' && mode === 'window' ? win.dragProps : null} />
        <div className="relative flex min-h-0 flex-1">
          <div className="relative min-w-0 flex-1 bg-black">
            <CallStage dc={dc} boardRef={boardRef} canvasRef={canvasRef} annotate={annotate} view={view} />
            <Dropped dc={dc} />
            <WaitingNudge share={shareCtl} open={panel === 'participants'} />
            <HostDialogs ask={ask} setAsk={setAsk} handoff={handoff} dc={dc} onEndAll={host?.onEndAll} />
          </div>
          {panel && <SidePanel panel={panel} setPanel={setPanel} dc={dc} hands={hands} share={shareCtl} chat={chat} overlay={width < 900}
            host={{ hostId: handoff.hostId, canLower, canMakeHost: handoff.isHost, makeHost: handoff.transfer }} />}
        </div>
        <Toolbar dc={dc} where={where} narrow={narrow} guest={mode === 'standalone'} sharing={sharing} annotate={annotate} setAnnotate={setAnnotate} onClear={clear}
          panel={panel} setPanel={setPanel} unread={chat.unread} people={people} host={powers} pop={{ close: pop.close }} />
      </>
    );
  };

  const frame = () => {
    if (dc.phase === 'lobby') {
      const lobby = <CallLobby dc={dc} title={title} onCancel={dc.leave} guest={mode === 'standalone'} />;
      return mode === 'standalone' ? <GuestFrame title={title} card>{lobby}</GuestFrame> : <FullFrame wide={false} white>{lobby}</FullFrame>;
    }
    if (pop.root) {
      return (
        <>
          <PoppedPill title={title} onBack={pop.close} onLeave={() => hostLeave(pop.close)} />
          {createPortal(<div className="flex h-full w-full flex-col bg-[#0A1521]" data-testid="mm-popout-root">{inner('popout')}</div>, pop.root)}
        </>
      );
    }
    if (mode === 'mini') {
      return (
        <MiniFrame title={title} onExpand={onExpand}>
          <div className="relative h-full w-full bg-black">
            <CallStage dc={dc} boardRef={boardRef} canvasRef={canvasRef} annotate={false} compact />
            <Dropped dc={dc} />
            <Toolbar dc={dc} compact unread={chat.unread} setPanel={setPanel} onExpand={onExpand} onLeave={() => hostLeave(onExpand)} />
          </div>
        </MiniFrame>
      );
    }
    return <CallWindow win={win} floating={mode !== 'standalone'}>{inner('app')}</CallWindow>;
  };

  // The audio sits first in every layout, so React keeps those elements (no blip) when the frame changes.
  return <><div className="hidden"><RemoteAudio dc={dc} /></div>{frame()}</>;
}
